<template>
  <div class="company" :key="updateCardKey">
    <div v-if="!selectedCompany" class="no company-card">
      <span>Please select a company</span>
    </div>
    <div v-if="!!selectedCompany" class="company-info-container">
      <object-main-info
        :fields="fields"
        :identifier="cardObjectIdentifier"
        :identifier-label="$t('Name')"
        :editing-data="editingData"
        :show-edit-button="!creatingCompany && ((userHasPermission('company_update') && !companyIsParentCompany) || isSuperAdmin)"
        :card-label="showPermissions ? $tc('Permission', 2) + ': ' + company.name : cardLabel "
        :height="cardHeight"
        :expand-card="editingData || showPermissions"
        :submit-button-label="submitButtonLabel"
        :required-fields-filled="requiredFieldsAreFilled"
        showEditActionButtons
        @toggle-edit="toggleEdit"
        @save-object="saveObject"
        @update-object="updateCompany"
        @cancel-edit="discardChangesDialogIsOpen = true"
      >
        <template v-slot:object-image-slot v-if="!showPermissions">
          <img
            class="company-logo"
            :src="company ? company.image : null"
            alt="Company Image Icon"
          />
        </template>
        <template v-slot:buttons-before-edit-slot v-if="!showPermissions && !companyIsParentCompany">
          <custom-button
            :icon="'mdi-key'"
            :icon-size="13"
            :height="'20px'"
            :width="'20px'"
            :button-styling="buttonStyling"
            :disabled="company ? !company.company_id && !companyIsParentCompany : true"
            @buttonClick="togglePermissions"
          />
        </template>
        <template v-slot:before-fields-slot v-if="editingData && !showPermissions">
          <div class="edit-image-container">
            <input
              type="file"
              id="file"
              ref="file"
              @change="previewFile"
              accept="image/*"
              style="display: none"
            />
            <img
              class="company-logo"
              :src="company ? company.image : null"
              alt="Company Image Icon"
            />
            <custom-button
              :height="'20px'"
              :label="!creatingCompany ? $t('ChangePhoto') : $t('AddPhoto')"
              :button-styling="{...buttonStyling, 'padding': '0 5px', 'margin-right': '7px', 'font-size': '9px'}"
              @buttonClick="submitFile"
            />
            <custom-button
              :icon="'mdi-delete'"
              :icon-size="13"
              :width="'20px'"
              :height="'20px'"
              :button-styling="buttonStyling"
              @buttonClick="deleteCompanyImage"
            />
          </div>
        </template>
        <template v-slot:before-fields-slot v-else-if="showPermissions">
          <div class="permissionMatrixDiv">
            <permission-matrix :data="permissionsMatrix" :disabled="!editingData"></permission-matrix>
          </div>
        </template>
        <template v-slot:before-title-slot v-if="showPermissions">
          <custom-button
            :icon="'mdi-chevron-left'"
            :icon-size="14"
            :height="'16px'"
            :width="'16px'"
            :button-styling="{...buttonStyling, 'box-shadow': 'none'}"
            :button-color="'transparent'"
            :icon-color="'#29BDF0'"
            @buttonClick="togglePermissions"
          />
        </template>
      </object-main-info>
      <company-specific-info
        v-if="!editingData && !showPermissions"
        :users="company ? company.users : []"
        :roles="company ? company.roles : []"
        :workspaces="company ? company.workspaces : []"
      />
    </div>

    <loading-spinner
      :active.sync="loading"
      :is-full-page="false"
      color="#29BDF0"
      style="position: absolute;"
    />
    <dialog-choice
      :isOpen="saveChangesDialogIsOpen"
      :title="$t('SaveChanges')"
      :content="$t('SaveChangesContent')"
      @mainAction="saveObject"
      :mainActionLabel="$t('Confirm')"
      :secondaryActionLabel="$t('Cancel')"
      @secondaryAction="saveChangesDialogIsOpen = false"
      icon="mdi-content-save"
      iconColor="#29BDF0"
    />
    <dialog-choice
      :isOpen="discardChangesDialogIsOpen"
      :title="$t('DiscardChanges')"
      :content="$t('ClusterCreationConfirmation')"
      :mainActionLabel="$t('Confirm')"
      @mainAction="cancelEdit"
      :secondaryActionLabel="$t('Cancel')"
      @secondaryAction="discardChangesDialogIsOpen = false"
      icon="mdi-delete"
      iconColor="#29BDF0"
    />
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import { bus } from "@/main.js";
import _ from 'lodash';
import utils from "@/utils";
import { checkPermissionsMixin } from "@/mixins/checkPermissionsMixin";
import { objectManagementMixin } from "@/mixins/objectManagementMixin";
import { createEditPageMixin } from "@/mixins/management/createEditPageMixin";
import LoadingSpinner from "vue-loading-overlay";
import ObjectMainInfo from '@/newComponents/_Organisms/ObjectMainInfo';
import CompanySpecificInfo from '@/newComponents/SystemManagement/Companies/CompanySpecificInfo';
import DialogChoice from '../../_Molecules/DialogChoice.vue';
import CustomButton from '@/newComponents/_Atoms/CustomButton'
import PermissionMatrix from '@/newComponents/_Atoms/PermissionMatrix';
export default {
  name: "Company",

  components: {
    LoadingSpinner,
    DialogChoice,
    ObjectMainInfo,
    CustomButton,
    CompanySpecificInfo,
    PermissionMatrix,
  },

  mixins: [checkPermissionsMixin, objectManagementMixin, createEditPageMixin],

  props: {
    selectedCompany: {
      type: Object,
    },
  },

  data() {
    return {
      loading: false,
      company: null,
      editingData: false,
      originalCompany: {},
      specificRequiredFieldsAreFilled: false,
      dataToSend: {},
      actionToExecute: 'UPDATE_SELECTED_COMPANY',
      submitButtonLabel: 'Save',
      cardLabel: 'EditCompany',
      saveChangesDialogIsOpen: false,
      discardChangesDialogIsOpen: false,
      buttonStyling: {
        'padding': '0px',
        'drop-shadow': '0.85px 0.85px 1.75px rgba(0, 0, 0, 0.25)',
        'border-radius': '4px',
      },

      topCardHeight: {
        style: {
          maxHeight: '50%',
          height: 'auto',
        },
      },

      showPermissions: false,
      permissionsMatrix: [],

      companyTypes: [],

      updateCardKey: 0,
    }
  },

  mounted() {
    this.getCompaniesOfDifferentTypes();
    this.getCompanyTypeOptions();
  },

  watch: {
    selectedCompany(value) {
      if (value !== null && Object.keys(value).length === 0) { //if company is an empty object for creation
        this.setCreateCompanyVariables();
      } else if (!!value) { // if the company is a normal object to be edited
        this.setEditCompanyVariables();
      } else if (value === null) { // if there is no object
        this.clearVariables();
      }
    },

    editingData() {
      bus.$emit("toggle-create-edit-flag")
    },
  },

  computed: {
    requiredFieldsAreFilled() {
      return this.company ?
        !!this.company.name &&
        !!this.company.address &&
        !!this.company.email &&
        !!this.company.phone &&
        !!this.company.company_level_id &&
        (!!this.company.company_id || this.companyIsParentCompany) : false;
    },

    creatingCompany() {
      return this.actionToExecute === 'addCompany';
    },

    fields() {
      let fields = [];
      if (this.company !== null && !this.showPermissions) {
        fields = [
          {
            icon: "mdi-map-marker",
            label: this.$i18n.tc("Address", 1),
            value: this.company.address,
            varName: 'address',
            inputType: 'text',
            disabled: false,
            required: true,
            size: '100%',
          },
          {
            icon: "mdi-email",
            label: this.$i18n.t("Email"),
            value: this.company.email,
            varName: 'email',
            inputType: 'text',
            disabled: false,
            required: true,
            size: '100%',
          },
          {
            icon: "mdi-phone",
            label: this.$i18n.t("Phone"),
            value: this.company.phone,
            varName: 'phone',
            inputType: 'text',
            disabled: false,
            required: true,
            size: '100%',
          },
          {
            icon: "label",
            label: this.$i18n.tc("Level"),
            value: !this.editingData
              ? this.parseSelectValue([this.company.company_level_id], this.companyTypes)
              : this.company.company_level_id,
            varName: 'company_level_id',
            inputType: 'select',
            disabled: this.companyIsParentCompany,
            required: true,
            options: this.companyTypes,
            clearable: false,
            size: '100%',
          },
          {
            icon: "mdi-key",
            label: this.$i18n.tc("TwoFactorAuthenticationRequired"),
            value: this.editingData
              ? this.company['2fa_required'] ? 'True' : 'False'
              : this.company['2fa_required'] ? this.$i18n.t('Yes') : this.$i18n.t('No'),
            varName: '2fa_required',
            inputType: 'radio',
            disabled: true,
            required: false,
            radioOptions: [{ value: 'True', label: this.$i18n.t('Yes') }, { value: 'False', label: this.$i18n.t('No') }],
            size: this.editingData ? '50%' : '100%',
          },
        ];

        if (!this.companyIsParentCompany) {
          fields.splice(4, 0,
            {
              icon: "mdi-clipboard-check",
              label: this.$i18n.tc("Status"),
              value: this.editingData
                ? this.company.status ? 'True' : 'False'
                : this.company.status ? this.$i18n.t('Active') : this.$i18n.t('Inactive'),
              varName: 'status',
              inputType: 'radio',
              disabled: false,
              required: true,
              radioOptions: [{ value: 'True', label: this.$i18n.t('Active') }, { value: 'False', label: this.$i18n.t('Inactive') }],
              size: this.editingData ? '50%' : '100%',
            }
          )
        }

        if (this.editingData && !this.companyIsParentCompany) {
          fields.push(
            {
              icon: "mdi-key",
              label: this.$i18n.tc("ParentCompany"),
              value: !this.editingData
                ? this.parseSelectValue([this.company.company_id], this.parentCompanyOptions)
                : this.company.company_id,
              varName: 'company_id',
              inputType: 'select',
              disabled: false,
              required: true,
              options: this.parentCompanyOptions,
              size: '100%',
            }
          );
        }

        if (this.editingData) {
          fields.splice(0, 0,
            {
              icon: "label",
              label: this.$i18n.t("Name"),
              value: this.company.name,
              varName: 'name',
              inputType: 'text',
              disabled: false,
              required: true,
              size: '100%',
            }
          )
        }
      }
      return fields;
    },

    defaultAvatarCompany() {
      return require("@/assets/icon_new-company.png");
    },

    parentCompanyOptions() {
      let options = [];
      let normalizedOptions = [];
      if (this.company.company_level_id === "2") {
        options = this.$store.getters.companies_type1;
      } else if (this.company.company_level_id === "3") {
        options = this.$store.getters.companies_type2;
      }

      options.forEach(option => {
        normalizedOptions.push({
          code: option.id,
          name: option.name
        })
      })

      return normalizedOptions;
    },

    cardObjectIdentifier() {
      if (this.company) {
        if (this.showPermissions) {
          return this.$i18n.tc("Permission", 2) + ': ' + this.company.name
        }
        return this.company.name;
      }
      return "";
    },

    cardHeight() {
      if (this.showPermissions || this.editingData) {
        return '100%'
      } else if (!this.companyIsParentCompany) {
        return '30%'
      } else {
        return '28%'
      }
    },

    companyIsParentCompany() {
      return this.company ? this.company.company_level_id === "1" : false;
    }
  },

  methods: {
    ...mapActions([
      "fetchCompanyByID",
      "GET_COMPANIES_OF_TYPE_1",
      "GET_COMPANIES_OF_TYPE_2",
      "fetchPermissionsMatrix"
    ]),

    /**
     * Get companies by types
     * @returns {Promise<void>}
     */
    async getCompaniesOfDifferentTypes() {
      await this.GET_COMPANIES_OF_TYPE_1();
      await this.GET_COMPANIES_OF_TYPE_2();
    },

    async getCompanyByID() {
      this.showPermissions = false;
      let id = this.selectedCompany.id;
      let includes = this.getIncludes();

      this.loading = true;
      try {
        this.company = await this.fetchCompanyByID({
          id: id,
          includes: includes
        });

        this.originalCompany = _.cloneDeep(this.company);
        this.getCompanyPermissionsMatrix(this.company);

      } catch (error) {
        await this.$router.replace("/error");
        console.error(error);
      } finally {
        this.loading = false;
      }
    },

    /**
     * Returns what should be included in the go fetch a company,
     * according to the user's permissions
     * @returns {string}
     */
    getIncludes() {
      let includes = "users,roles.permissions,workspaces";
      // if (this.userHasManagementPermission("user_company_list")) {
      //   includes += "users,";
      // }
      // if (this.userHasManagementPermission("company_permission_list")) {
      //   includes += "roles,";
      // }
      // if (this.userHasManagementPermission("workspace_company_list")) {
      //   includes += "workspaces,";
      // }
      return includes;
    },

    getCompanyPermissionsMatrix(company) {
      let adminRole = company.roles.find(role => role.role_level === "admin");
      if (!!adminRole) {
        this.permissionsMatrix = adminRole.permissions;
      }
    },

    updateCompany(updatedValue) {
      if(updatedValue.varName === "company_level_id"){
        this.company.company_id = null;
      }
      this.company[updatedValue.varName] = updatedValue.value;
    },

    cancelEdit() {
      this.discardChangesDialogIsOpen = false;
      this.editingData = false;
      if (this.company.uuid) {
        this.company = _.cloneDeep(this.originalCompany);
      } else {
        this.$emit('clear-selected-item');
      }
    },

    toggleEdit() {
      if (this.editingData) {
        this.discardChangesDialogIsOpen = true;
      } else {
        this.editingData = !this.editingData
      }
    },
    async saveObject() {

      this.saveChangesDialogIsOpen = false;
      try {
        this.loading = true;

        this.createDataToSend();

        if (Object.keys(this.dataToSend).length > 0) {
          let data = this.dataToSend;
          if (!this.creatingCompany) {
            this.dataToSend.uuid = this.company.uuid;
            data = {
              id: this.company.id,
              updatedData: this.dataToSend
            }
          }
          await this.$store.dispatch(this.actionToExecute, data);
          await this.refreshParentCompanyOptions();
        }

        this.editingData = false;

        if (!this.creatingCompany) {
          this.getCompanyByID();
          this.$toasted.global.toast_success(this.$i18n.t('CompanyUpdatedSuccessfully'));
        } else {
          this.$toasted.global.toast_success(this.$i18n.t('CompanyCreatedSuccessfully'));
        }

        bus.$emit('reload-companies');
      } catch (error) {
        console.error(error);
        this.handleError(error);
      } finally {
        this.loading = false;
        this.dataToSend = {};
      }
    },

    async refreshParentCompanyOptions() {
      await this.GET_COMPANIES_OF_TYPE_1();
      await this.GET_COMPANIES_OF_TYPE_2();
    },

    /** Fills the object to send to the server by putting a watcher in each variable of the object */
    createDataToSend() {
      Object.keys(this.company).forEach(key => {
        if (this.company[key] !== this.originalCompany[key]) {
          this.dataToSend[key] = this.company[key];
        }
      });

      this.dataToSend.permissions = this.permissionsMatrix;

      if (!this.creatingCompany) {
        delete this.dataToSend.roles;
        delete this.dataToSend.workspaces;
        delete this.dataToSend.users
      }
    },

    handleError(error) {
      let errorHandle = utils.errorHandler(error);
      if (typeof errorHandle === "object") {
        if (errorHandle.hasOwnProperty("email")) {
          this.$toasted.global.toast_error(this.$i18n.t('ValidEmail'));
        }
        if (errorHandle.hasOwnProperty("address")) {
          this.$toasted.global.toast_error(this.$i18n.t('ValidAdress'));
        }
        if (errorHandle.hasOwnProperty("name")) {
          this.$toasted.global.toast_error(this.$i18n.t('ValidName'));
        }
        if (errorHandle.hasOwnProperty("image")) {
          this.$toasted.global.toast_error(this.$i18n.t('ValidImage'));
        }
        if (errorHandle.hasOwnProperty("company_id")) {
          this.$toasted.global.toast_error(this.$i18n.t('ValidParentCompany'));
        }
        if (errorHandle.hasOwnProperty("permissions")) {
          this.$toasted.global.toast_error(this.$i18n.t('ValidPermissions'));
        }
      } else if (typeof errorHandle === "string") {
        this.$toasted.global.toast_error(errorHandle);
      }
    },

    setCreateCompanyVariables() {
      this.company = {
        name: "",
        address: "",
        email: "",
        phone: "",
        status: true,
        company_id: undefined,
        company_level_id: "2",
        image: this.defaultAvatarCompany,
        '2fa_required': false,
        action: "rud",
      };
      this.originalCompany = {};
      this.actionToExecute = 'addCompany';
      this.submitButtonLabel = 'Create';
      this.cardLabel = 'NewCompany';
      this.editingData = true;
      this.showPermissions = false;
    },

    setEditCompanyVariables() {
      this.getCompanyByID();
      this.actionToExecute = 'editCompanyByID';
      this.submitButtonLabel = 'Save';
      this.cardLabel = 'EditCompany';
      this.showPermissions = false;
    },

    clearVariables() {
      this.company = null;
      this.originalCompany = null;
      this.editingData = false;
    },

    requiredFieldsValidation() {
      return this.requiredFieldsAreFilled;
    },

    deleteCompanyImage() {
      this.updateCompany({ varName: 'image', value: this.defaultAvatarCompany});
    },

    /**
     * Initiates the process of submitting an image file.
     */
    submitFile() {
      this.$refs.file.click();
    },

    /**
     * Initiates the process of preview aof the submitted image file.
     */
    previewFile() {
      this.$emit("imageValidation", false);
      let file = this.$refs.file.files[0];
      if (!file) {
        return;
      }
      this.createPreview(file);
    },

    /**
     * Obtains a readable image from the file submitted.
     * @param file
     */
    createPreview(file) {
      let reader = new FileReader();

      reader.onload = e => {
        this.updateCompany({ varName: 'image', value: e.target.result });
      };
      reader.readAsDataURL(file);
    },

    togglePermissions() {
      this.showPermissions = !this.showPermissions;
      if (this.creatingCompany && this.permissionsMatrix.length === 0) {
        this.getPermissionsMatrix();
      }
    },

    async getPermissionsMatrix() {
      try {
        this.loading = true;
        const adminRoleId = !this.creatingCompany
          ? this.company.roles.find((role) => role.role_level === "admin")
          : null;
        this.permissionsMatrix = await this.fetchPermissionsMatrix(
          this.company.company_id,
          adminRoleId
        );
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
        this.updateCardKey++;
      }
    },

    async getCompanyTypeOptions() {
      try {
        this.loading = true;
        let companyTypes = this.$store.getters.getListOfOptionsByListName(
          'list_company_level',
          [],
          true
        );

        let normalizedCompanyTypes = [];
        companyTypes.forEach(type => {
          let optionDisabled = type.code === "1";
          normalizedCompanyTypes.push({code: type.code, name: type.client_label, disabled: optionDisabled})
        });

        this.companyTypes = normalizedCompanyTypes;
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
      }
    }
  },
}
</script>

<style scoped>
.company {
  height: 100%;
  max-height: 100%;
  background-color: #fff;
  position: relative;
  border-radius: 8px;
}

.permissionMatrixDiv {
  margin-top: 16px;
  padding-bottom: 16px;
}

.company-card {
  height: 100%;
}

.company-card {
  box-shadow: 0px 2px 2px 0px rgba(0, 0, 0, 0.25);
  border-radius: 8px;
}

.no.company-card {
  display: flex;
  flex-direction: column;
  align-content: center;
  justify-content: center;
}

.no.company-card > span {
  color: #29BDF0;
  text-align: center;
  font-size: 12px;
  font-weight: 600;
  line-height: normal;
}

.company-info-container {
  gap: 8px;
  display: flex;
  flex-direction: column;
  max-height: 100%;
  height: 100%;
}

.company-logo {
  background-color: white;
  border-radius: 8px;
  height: 40px;
  width: 40px;
  box-shadow: 1.75px 1.75px 4.75px 0px rgba(0, 0, 0, 0.25);
  margin-right: 16px;
  margin-left: 2px;
}

.edit-image-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  margin: 10px 0;
  width: 100%;
}
</style>



// WEBPACK FOOTER //
// src/newComponents/SystemManagement/Companies/Company.vue